import React, { CSSProperties, useMemo } from 'react';

import moment from 'moment';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import useDeviceDetect from 'hooks/useDeviceDetect';
import { getActiveChat, getReplys } from 'redux/chat/selectors';
import { selectAllMessages, togglePinMessage } from 'redux/chat/specific_chat.slices';
import { useAppSelector } from 'redux/hooks';
import {
  changeCurrentScreen,
  getCurrentScreenView,
} from 'redux/mobile_screen_flow/mobile_screen_flow.slice';
import { ReactComponent as UnpinIcon } from 'assets/icons/unpin.svg';

import ChatCard from '../ChatCard/ChatCard';

import './ChatInfo.scoped.scss';
import ProfilePicture from '../../../../clean/components/common/ProfilePicture/ProfilePicture';

interface Props {
  children?: React.ReactNode;
  style?: CSSProperties;
  tooltipElement?: React.ReactNode;
}

const ChatInfo: React.FC<Props> = ({ style }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const allMess = useAppSelector(selectAllMessages);
  const activeChat = useAppSelector(getActiveChat);
  const replys = useAppSelector(getReplys);

  const pinnedCount = useMemo(() => {
    return allMess?.filter(mess => mess.is_pinned === true) ?? [];
  }, [allMess]);

  const handlePined = e => {
    const payload = {
      data: {
        is_pinned: !e.is_pinned,
        id: e.id,
      },
      chatId: e.chat_id,
    };
    dispatch(togglePinMessage(payload));
  };

  const { isMobile } = useDeviceDetect();
  const currentScreenView = useAppSelector(getCurrentScreenView);

  const renderPinnedMessage = () => {
    // eslint-disable-next-line array-callback-return
    return (allMess ?? []).map(element => {
      if (element.is_pinned === true) {
        return (
          <div className="pinned-line-container" key={element.id}>
            <div className="pinned-container">
              <div className="pinned-elements">
                <div className="avatar">
                  <ProfilePicture userId={element.user_id} />
                </div>
                <div>
                  <p className="pinned-name">
                    {`${element.user?.first_name ?? ''} ${element.user?.last_name ?? ''}`}
                  </p>
                  <p className="pinned-body">{element.body}</p>
                  <p className="pinned-date">{moment(element.created_at).fromNow()}</p>
                </div>
              </div>
              <UnpinIcon className="unpin-icon" onClick={() => handlePined(element)} />
            </div>
            <div className="line" />
            <div />
          </div>
        );
      }
    });
  };
  const renderReplys = () => {
    return replys.map(element => {
      return (
        <div className="pinned-line-container" key={element.id}>
          <div className="pinned-container">
            <div className="pinned-elements">
              <div className="avatar">
                <ProfilePicture userId={element.user.id} />
              </div>
              <div>
                <p className="pinned-name">
                  {`${element.user?.first_name ?? ''} ${element.user?.last_name ?? ''}`}
                </p>
                <p className="pinned-body">{element.body}</p>
                <p className="pinned-date">{moment(element.created_at).fromNow()}</p>
              </div>
            </div>
          </div>
          <div className="line" />
          <div />
        </div>
      );
    });
  };
  const renderMembers = () => {
    const sortedChatMembers = _.orderBy(
      activeChat.participants,
      [el => el.user?.full_name?.toLowerCase() ?? ''],
      ['asc']
    );

    return sortedChatMembers.map(element => {
      return (
        <div className="pinned-line-container" key={element.id}>
          <div className="pinned-container">
            <div className="pinned-elements">
              <div className="avatar">
                <ProfilePicture userId={element.user?.id} />
              </div>

              <div>
                <p className="pinned-name">{element.user?.full_name}</p>
              </div>
            </div>
          </div>
          <div />
        </div>
      );
      // }
    });
  };

  const getTitle = (): string => {
    switch (currentScreenView) {
      case 'pinned':
        return `${t('chat:Pinned_Messages.Pinned_Messages')} (${pinnedCount.length})`;
      case 'members':
        return `${t('common:chat_members_text')} (${activeChat?.participants?.length})`;
      case 'replys':
        return `${t('common:chat_thread_text')} (${replys.length})`;
      default:
        return '';
    }
  };

  return (
    <div className={'chat-info-container'} style={style}>
      <ChatCard>
        <ChatCard.Header
          title={getTitle()}
          backButton={!!isMobile}
          membersButton={false}
          pinButton={false}
          goToScreen={() => dispatch(changeCurrentScreen('chat'))}
        />
      </ChatCard>

      <ChatCard.Body
        style={{
          overflowY: 'scroll',
          height: 'calc(100vh - 140px)',
        }}
      >
        {currentScreenView === 'pinned' && renderPinnedMessage()}
        {currentScreenView === 'members' && renderMembers()}
        {currentScreenView === 'replys' && renderReplys()}
      </ChatCard.Body>
    </div>
  );
};

export default ChatInfo;
